import type { TodoNoteCategoryDraft, TodoNoteItemDraft } from '../types/todoNote';

export function normalizeTodoCategoryName(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

function categoryNameKey(name: string): string {
  return normalizeTodoCategoryName(name).toLowerCase();
}

function itemCloneKey(it: TodoNoteItemDraft): string {
  return JSON.stringify({
    title: (it.title ?? '').trim(),
    body: (it.body ?? '').trim(),
    misc: (it.misc ?? '').trim(),
    due_kind: it.due_kind,
    due_date: it.due_date,
    category_id: it.category_id,
    completed: Boolean(it.completed),
    is_routine: Boolean(it.is_routine),
    is_favorite: Boolean(it.is_favorite),
  });
}

/** 같은 이름 카테고리를 하나로 합치고, 복제된 블럭은 한 줄만 남긴다. */
export function mergeDuplicateTodoCategories(params: {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deletedCategoryIds: string[];
  deletedItemIds?: string[];
  /** 이 id를 같은 이름 그룹의 대표로 유지 */
  preferCategoryId?: string | null;
}): {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deletedCategoryIds: string[];
  deletedItemIds: string[];
  categoryInputPatches: Record<string, string>;
} {
  const deletedCategoryIds = [...params.deletedCategoryIds];
  const deletedItemIds = [...(params.deletedItemIds ?? [])];
  const categoryInputPatches: Record<string, string> = {};
  const byName = new Map<string, TodoNoteCategoryDraft[]>();
  for (const c of params.categories) {
    const key = categoryNameKey(c.name);
    if (!key) continue;
    const arr = byName.get(key) ?? [];
    arr.push(c);
    byName.set(key, arr);
  }

  const keepIds = new Set<string>();
  const remap = new Map<string, string>();

  Array.from(byName.values()).forEach((group: TodoNoteCategoryDraft[]) => {
    if (group.length === 1) {
      keepIds.add(group[0].id);
      return;
    }
    const preferred =
      (params.preferCategoryId
        ? group.find((c: TodoNoteCategoryDraft) => c.id === params.preferCategoryId)
        : undefined) ??
      group.slice().sort((a: TodoNoteCategoryDraft, b: TodoNoteCategoryDraft) => {
        const au = a.updated_at ?? '';
        const bu = b.updated_at ?? '';
        if (au !== bu) return bu.localeCompare(au);
        return a.sort_order - b.sort_order || a.id.localeCompare(b.id);
      })[0];
    keepIds.add(preferred.id);
    group.forEach((c: TodoNoteCategoryDraft) => {
      if (c.id === preferred.id) return;
      remap.set(c.id, preferred.id);
      if (!deletedCategoryIds.includes(c.id)) deletedCategoryIds.push(c.id);
    });
  });

  const categories = params.categories.filter((c) => keepIds.has(c.id));
  const remappedItemIds = new Set(
    params.items
      .filter((it) => Boolean(it.category_id && remap.has(it.category_id)))
      .map((it) => it.id)
  );
  const remappedItems = params.items.map((it) => {
    const nextCat = it.category_id ? remap.get(it.category_id) : undefined;
    if (!nextCat) return it;
    return { ...it, category_id: nextCat };
  });

  const keptNameById = new Map(categories.map((c) => [c.id, c.name]));
  for (const it of remappedItems) {
    const name = it.category_id ? keptNameById.get(it.category_id) : undefined;
    if (name) categoryInputPatches[it.id] = name;
  }

  const orderedItems = [
    ...remappedItems.filter((it) => !remappedItemIds.has(it.id)),
    ...remappedItems.filter((it) => remappedItemIds.has(it.id)),
  ];
  const seenClone = new Set<string>();
  const items: TodoNoteItemDraft[] = [];
  for (const it of orderedItems) {
    if (deletedItemIds.includes(it.id)) continue;
    const key = itemCloneKey(it);
    if (remappedItemIds.has(it.id) && seenClone.has(key)) {
      if (!deletedItemIds.includes(it.id)) deletedItemIds.push(it.id);
      continue;
    }
    seenClone.add(key);
    items.push(it);
  }

  return { categories, items, deletedCategoryIds, deletedItemIds, categoryInputPatches };
}

export function applyTodoCategoryModalConfirm(params: {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deletedCategoryIds: string[];
  deletedItemIds?: string[];
  payload: { name: string; color: string; categoryId?: string | null };
  defaultCategoryName: string;
  userId: string;
  workspaceId: string | null;
  generateId: () => string;
}): {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deletedCategoryIds: string[];
  deletedItemIds: string[];
  categoryInputPatches: Record<string, string>;
} | null {
  const normalizedName = normalizeTodoCategoryName(params.payload.name);
  const color = params.payload.color;
  const defaultName = normalizeTodoCategoryName(params.defaultCategoryName);
  const editId = params.payload.categoryId?.trim() || null;
  const existingById = editId ? params.categories.find((c) => c.id === editId) : undefined;
  const existingByName = params.categories.find(
    (c) => categoryNameKey(c.name) === categoryNameKey(normalizedName)
  );

  let nextCategories = params.categories;
  let preferCategoryId = editId;
  let categoryInputPatches: Record<string, string> = {};

  if (existingById) {
    const isDefault = categoryNameKey(existingById.name) === categoryNameKey(defaultName);
    const nextName = isDefault ? existingById.name : normalizedName || existingById.name;
    if (!isDefault && (!nextName || categoryNameKey(nextName) === categoryNameKey(defaultName))) {
      return null;
    }
    nextCategories = params.categories.map((c) =>
      c.id === existingById.id ? { ...c, name: nextName, color } : c
    );
    preferCategoryId = existingById.id;
    if (!isDefault) {
      for (const it of params.items) {
        if (it.category_id === existingById.id) categoryInputPatches[it.id] = nextName;
      }
    }
  } else if (existingByName) {
    nextCategories = params.categories.map((c) =>
      c.id === existingByName.id ? { ...c, color } : c
    );
    preferCategoryId = existingByName.id;
  } else {
    if (!normalizedName || categoryNameKey(normalizedName) === categoryNameKey(defaultName)) {
      return null;
    }
    const nextOrder = params.categories.length
      ? Math.max(...params.categories.map((c) => c.sort_order)) + 1
      : 0;
    const row: TodoNoteCategoryDraft = {
      id: params.generateId(),
      user_id: params.userId,
      workspace_id: params.workspaceId,
      name: normalizedName,
      color,
      sort_order: nextOrder,
      note_memo: '',
    };
    nextCategories = [...params.categories, row].sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)
    );
    preferCategoryId = row.id;
  }

  const merged = mergeDuplicateTodoCategories({
    categories: nextCategories,
    items: params.items,
    deletedCategoryIds: params.deletedCategoryIds,
    deletedItemIds: params.deletedItemIds,
    preferCategoryId,
  });
  return {
    ...merged,
    categoryInputPatches: { ...categoryInputPatches, ...merged.categoryInputPatches },
  };
}
